import { FC, useMemo, useState } from "react";
import { StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import MultiStockCardHorizontal from "../MultiStockCard/MultiStockCardHorizontal";
import Skeleton from "react-loading-skeleton";
import { log } from "console";

// Enhanced type definitions
export interface PackageContentItem {
  day: number;
  time?: string;
  type: 'property' | 'experience';
  uuid: string;
  notes?: string;
  title?: string;
  // Optional image/media fields for fallback rendering
  cover?: string;
  media?: string[];
  // Optional property-specific fields
  name?: string;
  address?: string;
  building?: string;
  description?: string;
  weekday_price?: number | string;
  currency?: string;
  rating?: number | string;
  reviews_count?: number | string;
  // Optional experience-specific fields
  price?: number | string;
}

export interface Property {
  property_uuid: string;
  slug?: string;
  name: string;
  address?: string;
  building?: string;
  description?: string;
  cover?: string;
  media?: string[];
  gallery_images?: string[];
  weekday_price?: number | string;
  currency?: string;
  rating?: number | string;
  reviews_count?: number | string;
  uuid: string;
}

export interface Experience {
  uuid: string;
  name?: string;
  title?: string;
  description?: string;
  cover?: string;
  media?: string[];
  gallery_images?: string[];
  price?: number | string;
  currency?: string;
  rating?: number | string;
  reviews_count?: number | string;
  [key: string]: any;
}

export interface SectionStayDaysProps {
  linkedProperties?: Property[];
  linkedExperiences?: Experience[];
  package_content?: PackageContentItem[];
  duration_days?: number;
  isLoading?: boolean;
  className?: string;
}

export interface DayContent {
  day: number;
  properties: (Property & { time: string; notes: string })[];
  experiences: (Experience & { time: string; notes: string })[];
}

// Type guard functions for validation
const isValidPackageContentItem = (item: any): item is PackageContentItem => {
  return (
    item &&
    typeof item.day === 'number' &&
    item.day >= 1 &&
    (item.type === 'property' || item.type === 'experience') &&
    typeof item.uuid === 'string'
  );
};

const isValidProperty = (property: any): property is Property => {
  return (
    property &&
    typeof property.property_uuid === 'string' &&
    typeof property.name === 'string'
  );
};

const isValidExperience = (experience: any): experience is Experience => {
  return (
    experience &&
    typeof experience.uuid === 'string'
  );
};

// Helper function to sort items by time
const sortByTime = <T extends { time: string }>(items: T[]): T[] => {
  return items.sort((a, b) => {
    if (!a.time && !b.time) return 0;
    if (!a.time) return 1;
    if (!b.time) return -1;
    return a.time.localeCompare(b.time);
  });
};

const SectionStayDays: FC<SectionStayDaysProps> = ({
  linkedProperties,
  linkedExperiences = [],
  package_content = [],
  duration_days = 1,
  isLoading = false,
  className = "",
}) => {
  // State for accordion functionality
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([1])); // Expand day 1 by default

  const toggleDay = (dayNumber: number) => {
    const newExpandedDays = new Set(expandedDays);
    if (newExpandedDays.has(dayNumber)) {
      newExpandedDays.delete(dayNumber);
    } else {
      newExpandedDays.add(dayNumber);
    }
    setExpandedDays(newExpandedDays);
  };

  // Memoized data processing for better performance
  const { daysContent, hasAnyContent } = useMemo(() => {
    // Ensure linkedExperiences is always an array and validate
    const safeLinkedExperiences = Array.isArray(linkedExperiences)
      ? linkedExperiences
      : [];

    // Ensure linkedProperties is always an array and validate
    const safeLinkedProperties = Array.isArray(linkedProperties)
      ? linkedProperties
      : [];


    // Create lookup maps for better performance
    const propertyMap = new Map<string, Property>();
    safeLinkedProperties.forEach(prop => {
      if (prop.property_uuid) {
        propertyMap.set(prop.property_uuid, prop);
      }
      if (prop.uuid) {
        propertyMap.set(prop.uuid, prop);
      }
    });

    const experienceMap = new Map<string, Experience>();
    safeLinkedExperiences.forEach(exp => {
      if (exp.experience_uuid) {
        experienceMap.set(exp.experience_uuid, exp);
      }
      if (exp.uuid) {
        experienceMap.set(exp.uuid, exp);
      }
    });

    console.log("propertyMap:", propertyMap);
    console.log("package_content:", package_content);

    // Initialize days array
    const days: DayContent[] = Array.from({ length: duration_days }, (_, i) => ({
      day: i + 1,
      properties: [],
      experiences: [],
    }));

    // Track assigned UUIDs for latter unassigned items calculation
    const assignedUuids = new Set<string>();

    // Process package_content with validation
    const validPackageContent = package_content.filter(isValidPackageContentItem);
    validPackageContent.forEach((item) => {
      if (item.day >= 1 && item.day <= duration_days) {
        const dayIndex = item.day - 1;
        assignedUuids.add(item.uuid);

        if (item.type === 'property') {
          const linkedProperty = propertyMap.get(item.uuid);
          console.log("Looking for property with uuid:", item.uuid, "Found:", linkedProperty);

          if (linkedProperty && isValidProperty(linkedProperty)) {
            // Use linked property data with time/notes from package_content
            days[dayIndex].properties.push({
              ...linkedProperty,
              uuid: linkedProperty.property_uuid, // Ensure uuid field exists
              time: item.time || "",
              notes: item.notes || "",
            });
            console.log("Added property to day", dayIndex);
          } else {
            console.log("Property validation failed for:", linkedProperty);
          }
        } else if (item.type === 'experience') {
          const experience = experienceMap.get(item.uuid);

          if (experience) {
            // Use linked experience data with time/notes from package_content
            days[dayIndex].experiences.push({
              ...experience,
              time: item.time || "",
              notes: item.notes || "",
            });
          }
        }
      }
    });

    // Sort items within each day by time
    days.forEach(day => {
      day.properties = sortByTime(day.properties);
      day.experiences = sortByTime(day.experiences);
    });

    const hasAnyContent = days.some(day =>
      day.properties.length > 0 || day.experiences.length > 0
    );

    return {
      daysContent: days,
      hasAnyContent,
    };
  }, [linkedProperties, linkedExperiences, package_content, duration_days]);

  if (isLoading) {
    return (
      <div className={`listingSection__wrap ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">Il tuo itinerario</h2>
          <div className="flex items-center space-x-4 text-sm text-neutral-600 dark:text-neutral-400">
            <Skeleton className="w-12 h-4" />
            <Skeleton className="w-16 h-4" />
            <Skeleton className="w-20 h-4" />
          </div>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="space-y-6">
          {Array(duration_days).fill(0).map((_, index) => (
            <div key={index} className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              <div className="bg-gradient-to-r from-neutral-50 to-neutral-100 dark:from-neutral-900/20 dark:to-neutral-800/20 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Skeleton className="w-12 h-12 rounded-full" />
                    <div>
                      <Skeleton className="w-24 h-6 mb-2" />
                      <Skeleton className="w-32 h-4" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Skeleton className="w-8 h-6 rounded-full" />
                    <Skeleton className="w-8 h-6 rounded-full" />
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="w-6 h-6 rounded-full" />
                    <Skeleton className="w-20 h-5" />
                  </div>
                  <div className="space-y-4">
                    <Skeleton className="w-full h-40 rounded-lg" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="w-6 h-6 rounded-full" />
                    <Skeleton className="w-24 h-5" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Skeleton className="w-full h-32 rounded-lg" />
                    <Skeleton className="w-full h-32 rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!hasAnyContent) {
    return (
      <div className={`listingSection__wrap ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">Il tuo itinerario</h2>
          <div className="flex items-center space-x-4 text-sm text-neutral-600 dark:text-neutral-400">
            <span className="flex items-center space-x-1">
              <span className="font-medium">0</span>
              <span>giorni</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="font-medium">0</span>
              <span>alloggi</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="font-medium">0</span>
              <span>esperienze</span>
            </span>
          </div>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Nessun itinerario disponibile
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400 max-w-md">
            Questo pacchetto non ha ancora attività assegnate. Controlla più tardi o contatta l'assistenza per maggiori informazioni.
          </p>
        </div>
      </div>
    );
  }

  // Calculate summary statistics
  const totalProperties = linkedProperties ? linkedProperties.length : 0;
  const totalExperiences = linkedExperiences ? linkedExperiences.length : 0;
  const totalDays = daysContent.filter(day => day.properties.length > 0 || day.experiences.length > 0).length;

  return (
    <div className={`listingSection__wrap ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">Il tuo itinerario</h2>
        <div className="flex items-center space-x-4 text-sm text-neutral-600 dark:text-neutral-400">
          <span className="flex items-center space-x-1">
            <span className="font-medium">{totalDays}</span>
            <span>giorni</span>
          </span>
          <span className="flex items-center space-x-1">
            <span>-</span>
          </span>
          <span className="flex items-center space-x-1">
            <span className="font-medium">{totalProperties}</span>
            <span>alloggi</span>
          </span>
          <span className="flex items-center space-x-1">
            <span className="font-medium">{totalExperiences}</span>
            <span>esperienze</span>
          </span>
        </div>
      </div>
      <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
      <div className="text-neutral-6000 dark:text-neutral-300 max-w-full">
        <div className="space-y-8">
          {/* Day-by-day content */}
          {daysContent.map((day) => {
            console.log("Rendering day: ", day.day, " with properties: ", day.properties.length, " and experiences: ", day.experiences.length);
            if (day.properties.length === 0 && day.experiences.length === 0) {
              return null;
            }

            const dayTotalItems = day.properties.length + day.experiences.length;

            return (
              <div key={day.day} className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                {/* Enhanced Day header - Clickable */}
                <button
                  onClick={() => toggleDay(day.day)}
                  className="w-full bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 hover:opacity-80 transition-opacity text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-12 h-12 bg-primary-500 text-white rounded-full font-semibold text-lg shadow-lg">
                        {day.day}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                          Giorno {day.day}
                        </h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {dayTotalItems} {dayTotalItems === 1 ? 'attività' : 'attività'}
                          {day.properties.length > 0 && ` • ${day.properties.length} ${day.properties.length === 1 ? 'alloggio' : 'alloggi'}`}
                          {day.experiences.length > 0 && ` • ${day.experiences.length} ${day.experiences.length === 1 ? 'esperienza' : 'esperienze'}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Triangle icon */}
                      <div
                        className={`transition-transform duration-300 ${expandedDays.has(day.day) ? 'rotate-180' : ''
                          }`}
                      >
                        <svg
                          className="w-4 h-4 text-neutral-600 dark:text-neutral-400"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M7 10l5 5 5-5z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Collapsible content */}
                {expandedDays.has(day.day) && (
                  <div className="p-6 space-y-6">
                    {/* Properties for this day */}
                    {day.properties.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">
                            Alloggio{day.properties.length > 1 ? 'i' : ''}
                          </h4>
                        </div>
                        <div className="space-y-4">
                          {day.properties.map((property, index) => (
                            <div key={property.property_uuid || index} className="relative">
                              {property.time && (
                                <div className="absolute -top-2 left-2 z-10 bg-blue-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
                                  {property.time}
                                </div>
                              )}
                              <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-1">
                                <MultiStockCardHorizontal
                                  id={property.property_uuid || index}
                                  cover={property.cover || ""}
                                  images={
                                    property.gallery_images && property.gallery_images.length
                                      ? property.gallery_images
                                      : property.cover
                                        ? [property.cover]
                                        : []
                                  }
                                  title={property.name || ""}
                                  address={`${property.address || ""}${property.building ? ", " + property.building : ""}`}
                                  description={property.description || ""}
                                  price={property.weekday_price ? Number(property.weekday_price) : 0}
                                  currency={property.currency || "EUR"}
                                  rating={property.rating ? Number(property.rating) : 0}
                                  reviewCount={property.reviews_count ? Number(property.reviews_count) : 0}
                                  href={stockDetailPath(StockNumericType.STAY, property.slug || property.property_uuid)}
                                  className="w-full"
                                />
                              </div>
                              {property.notes && (
                                <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                  <div className="flex items-start space-x-2">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Note</p>
                                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">{property.notes}</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Experiences for this day */}
                    {day.experiences.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">
                            Esperienze
                          </h4>
                        </div>
                        <div className="space-y-4">
                          {day.experiences.map((experience, index) => (
                            <div key={experience.uuid || index} className="relative">
                              {experience.time && (
                                <div className="absolute -top-2 left-2 z-10 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
                                  {experience.time}
                                </div>
                              )}
                              <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-1">
                                <MultiStockCardHorizontal
                                  id={experience.uuid || index}
                                  cover={experience.cover || ""}
                                  images={experience.gallery_images || experience.media || []}
                                  title={experience.name || experience.title || ""}
                                  address={experience.description || ""}
                                  description=""
                                  price={experience.price ? Number(experience.price) : 0}
                                  currency={experience.currency || "EUR"}
                                  rating={experience.rating ? Number(experience.rating) : 0}
                                  reviewCount={experience.reviews_count ? Number(experience.reviews_count) : 0}
                                  href={`/experience/${experience.uuid}`}
                                  className="w-full"
                                />
                              </div>
                              {experience.notes && (
                                <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <div className="flex items-start space-x-2">
                                    <span className="text-green-500 text-sm mt-0.5">📝</span>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-green-800 dark:text-green-200">Note</p>
                                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">{experience.notes}</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}


        </div>
      </div>
    </div>
  );
};

export default SectionStayDays;
